import { useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Stack, Switch, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import { Navigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppLayout } from "@/components/AppLayout";
import { useAuth } from "@/auth/AuthContext";
import { createUser, fetchUsers, resendInvite, setUserActive, setUserPermission, type CloseReviewFlag, type CollectionsFlag, type EmailFlag, type PermissionFlag, type PosterFlag } from "@/api/users";
import type { User } from "@/types";

function errorMessage(e: unknown): string {
  return (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Something went wrong.";
}

export function UsersPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");

  const users = useQuery({ queryKey: ["users"], queryFn: fetchUsers });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["users"] });

  const add = useMutation({
    mutationFn: () => createUser({ email: email.trim(), name: name.trim() || undefined }),
    onSuccess: () => {
      setEmail("");
      setName("");
      invalidate();
    },
  });

  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) => setUserActive(id, active),
    onSuccess: invalidate,
  });

  const permission = useMutation({
    mutationFn: ({ id, flag, value }: { id: number; flag: PermissionFlag; value: boolean }) => setUserPermission(id, flag, value),
    onSuccess: invalidate,
  });

  // A poster switch. Super admins grant these but can't hold them, and nobody
  // inactive can post, so the switch is off and locked for both.
  const posterSwitch = (u: User, flag: PosterFlag, label: string) => {
    const locked = u.super_admin || !u.active;
    return (
      <Tooltip title={u.super_admin ? "Super admins can't post (segregation of duties)" : !u.active ? "Reactivate first" : ""}>
        <span>
          <Switch
            size="small"
            checked={Boolean(u[flag])}
            disabled={locked || permission.isPending}
            onChange={(e) => permission.mutate({ id: u.id, flag, value: e.target.checked })}
            inputProps={{ "aria-label": `${label} for ${u.email}` }}
          />
        </span>
      </Tooltip>
    );
  };

  // A Collections switch. Not a segregation-of-duties control, so super admins
  // may hold it; only inactive users are locked.
  const collectionsSwitch = (u: User, flag: CollectionsFlag | CloseReviewFlag | EmailFlag, label: string) => (
    <Tooltip title={!u.active ? "Reactivate first" : ""}>
      <span>
        <Switch
          size="small"
          checked={Boolean(u[flag])}
          disabled={!u.active || permission.isPending}
          onChange={(e) => permission.mutate({ id: u.id, flag, value: e.target.checked })}
          inputProps={{ "aria-label": `${label} for ${u.email}` }}
        />
      </span>
    </Tooltip>
  );

  const [invitedId, setInvitedId] = useState<number | null>(null);
  const resend = useMutation({
    mutationFn: (id: number) => resendInvite(id),
    onSuccess: (_data, id) => setInvitedId(id),
  });

  // Super-admins only — the API enforces this too, but don't even render for others.
  if (user && !user.super_admin) return <Navigate to="/" replace />;

  return (
    <AppLayout>
      <Typography variant="h5" gutterBottom>Users</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sign-in is invite-only. Add a Freedom Solar email to grant access — they'll get an invite email with a
        link to log in with Google. Deactivate to revoke access; it signs the person out immediately and is reversible.
        The poster switches decide who may post Lightreach AR imports to NetSuite and who may post Payments to
        Sunrise (and gets its daily email). Super admins can grant them but never hold them. The
        Collections switches decide who may send customers payment reminders and who may create their invoices.
      </Typography>

      {resend.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(resend.error)}</Alert>}
      {invitedId !== null && !resend.isPending && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInvitedId(null)}>Invite email sent.</Alert>
      )}

      <Box component="form" onSubmit={(e) => { e.preventDefault(); add.mutate(); }} sx={{ mb: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="flex-start">
          <TextField
            label="Email" type="email" size="small" required value={email}
            onChange={(e) => setEmail(e.target.value)} sx={{ minWidth: 280 }}
          />
          <TextField
            label="Name (optional)" size="small" value={name}
            onChange={(e) => setName(e.target.value)} sx={{ minWidth: 200 }}
          />
          <Button type="submit" variant="contained" disabled={add.isPending || email.trim() === ""}>
            {add.isPending ? "Adding…" : "Add user"}
          </Button>
        </Stack>
        {add.isError && <Alert severity="error" sx={{ mt: 1 }}>{errorMessage(add.error)}</Alert>}
      </Box>

      {toggle.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(toggle.error)}</Alert>}
      {permission.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(permission.error)}</Alert>}

      {users.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}><CircularProgress /></Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Lightreach AR poster</TableCell>
              <TableCell align="center">Payments to Sunrise poster</TableCell>
              <TableCell align="center">Send reminder</TableCell>
              <TableCell align="center">Create invoice</TableCell>
              <TableCell align="center">Close Review</TableCell>
              <TableCell align="center">Email replies</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.data?.map((u: User) => (
              <TableRow key={u.id} hover>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.name ?? "—"}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    <Chip size="small" label={u.active ? "Active" : "Inactive"} color={u.active ? "success" : "default"} />
                    {u.super_admin && <Chip size="small" label="Admin" color="info" variant="outlined" />}
                  </Stack>
                </TableCell>
                <TableCell align="center">{posterSwitch(u, "lightreach_ar_poster", "Lightreach AR poster")}</TableCell>
                <TableCell align="center">{posterSwitch(u, "payment_sync_poster", "Payments to Sunrise poster")}</TableCell>
                <TableCell align="center">{collectionsSwitch(u, "collections_reminder_sender", "Send reminder")}</TableCell>
                <TableCell align="center">{collectionsSwitch(u, "collections_invoice_creator", "Create invoice")}</TableCell>
                <TableCell align="center">{collectionsSwitch(u, "close_reviewer", "Close Review")}</TableCell>
                <TableCell align="center">{collectionsSwitch(u, "email_responder", "Email replies")}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    {u.active && !u.super_admin && (
                      <Button
                        size="small"
                        disabled={resend.isPending}
                        onClick={() => resend.mutate(u.id)}
                      >
                        Resend invite
                      </Button>
                    )}
                    {u.active ? (
                      <Button
                        size="small" color="error"
                        disabled={toggle.isPending || u.super_admin || u.id === user?.id}
                        onClick={() => toggle.mutate({ id: u.id, active: false })}
                      >
                        Deactivate
                      </Button>
                    ) : (
                      <Button
                        size="small" disabled={toggle.isPending}
                        onClick={() => toggle.mutate({ id: u.id, active: true })}
                      >
                        Reactivate
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {users.data?.length === 0 && (
              <TableRow><TableCell colSpan={6}><em>No users yet.</em></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </AppLayout>
  );
}
